import moment from 'moment';
import React from 'react';
import styled from 'styled-components';

import { Footer, InternalLink, colors, media, Text, Label } from '../ui-components';

import SvgCrederaLogoNameLight from '../svg-components/svg-credera-logo-name-light';
import linkedin from '../assets/icons/social/linkedin-white.svg';
import instagram from '../assets/icons/social/instagram-white.svg';
import youtube from '../assets/icons/social/youtube-white.svg';
import FOOTER from '../static-content/footer.json';
import { checkUrlLocale } from '../utils/check-url-locale';

/**
 * Component that renders the footer
 *
 * @component AppFooter
 * 
 * @return {JSX}
 */
const AppFooter = () => {
  let footerGeo = '';

  if (typeof window !== 'undefined') {
    footerGeo = checkUrlLocale();
  }

  const renderSocialSection = () => {
    return (
      <div>
        <SocialLinkContainer>
          <a href="https://www.linkedin.com/company/credera" target="_blank" rel="noreferrer" title="LinkedIn">
            <SocialIcon src={linkedin} alt="LinkedIn logo" />
          </a>
          <a href="https://www.instagram.com/lifeatcredera/" target="_blank" rel="noreferrer" title="Instagram">
            <SocialIcon src={instagram} alt="Instagram logo" />
          </a>
          <a
            href="https://www.youtube.com/channel/UCVgjc8oEkegCR2J6Lo0V62A"
            target="_blank"
            rel="noreferrer"
            title="Youtube"
          >
            <SocialIcon src={youtube} alt="Youtube logo" />
          </a>
        </SocialLinkContainer>
        <div>Copyright &copy; {moment().year()} Credera. All rights reserved.</div>
      </div>
    );
  };

  const renderLocaleLinks = () => {
    if(footerGeo === '/de-de') {
      return (
        <InternalColumn>
          <FooterColumnLabel>Legal</FooterColumnLabel>
          <FooterInternalLink to="/cookie-notice">Cookie Settings</FooterInternalLink>
          <FooterInternalLink to="/europe-asia-pacific-privacy-notice">Europe | Asia-Pacific Privacy Notice</FooterInternalLink>
          <FooterInternalLink to="/richtlinie-hinweisgeberschutzgesetz">
            Guideline Whistleblower Protection Act
          </FooterInternalLink>
          <FooterInternalLink to="/menschenrechtserklaerung">Human Rights</FooterInternalLink>
          <FooterInternalLink to="/code-of-conduct-fuer-lieferanten-und-geschaeftspartner">
            Code of Conduct
          </FooterInternalLink>
          <FooterInternalLink to="/impressum">Imprint</FooterInternalLink>
        </InternalColumn>
      );  
    } else if(footerGeo === '/en-gb') {
      return (
        <InternalColumn>
          <FooterColumnLabel>Legal</FooterColumnLabel>
          <FooterInternalLink to="/europe-asia-pacific-privacy-notice">Europe | Asia-Pacific Privacy Notice</FooterInternalLink>
          <FooterInternalLink to="/cookie-notice">Cookie Settings</FooterInternalLink>
          <FooterInternalLink to="/modern-slavery-statement">Modern Slavery Statement</FooterInternalLink>
          <FooterInternalLink to="/accessibility-statement">Accessibility Statement</FooterInternalLink>
        </InternalColumn>
      );
    } else if(footerGeo === '/en-au') {
      return (
        <InternalColumn>
          <FooterColumnLabel>Legal</FooterColumnLabel>
          <FooterInternalLink to="/europe-asia-pacific-privacy-notice">Europe | Asia-Pacific Privacy Notice</FooterInternalLink>
          <FooterInternalLink to="/cookie-notice">Cookie Settings</FooterInternalLink>
          <FooterInternalLink to="/modern-slavery-statement">Modern Slavery Statement</FooterInternalLink>
        </InternalColumn>
      );
    } else if(footerGeo === '/en-in') {
      return (
        <InternalColumn>
          <FooterColumnLabel>Legal</FooterColumnLabel>
          <FooterInternalLink to="/europe-asia-pacific-privacy-notice">Europe | Asia-Pacific Privacy Notice</FooterInternalLink>
          <FooterInternalLink to="/cookie-notice">Cookie Settings</FooterInternalLink>
        </InternalColumn>
      );
    } else {
      return (
        <InternalColumn>
          <FooterColumnLabel>Legal</FooterColumnLabel>
          <FooterInternalLink to="/privacy-notice">General Privacy Notice</FooterInternalLink>
          <FooterInternalLink to="/europe-asia-pacific-privacy-notice">
            Europe | Asia-Pacific Privacy Notice
          </FooterInternalLink>
          <FooterInternalLink to="/cookie-notice">Cookie Settings</FooterInternalLink>
        </InternalColumn>
      ); 
    }
  }

  return (
    <Footer>
      <LeftContent className="left-content">
        <div>
          <InternalLink to="/" aria-label="Back to Homepage" alt="Credera Logo">
            <SvgCrederaLogoNameLight />
          </InternalLink>
          <FooterText size="medium">{FOOTER.FOOTER}</FooterText>
        </div>
        <DesktopSocial>{renderSocialSection()}</DesktopSocial>
      </LeftContent>
      <RightContent>
        <InternalColumn>
          <FooterColumnLabel>Explore</FooterColumnLabel>
          <FooterInternalLink to="/services">Services</FooterInternalLink>
          <FooterInternalLink to="/industries">Industries</FooterInternalLink>
          <FooterInternalLink to="/partnerships">Partners</FooterInternalLink>
          <FooterInternalLink to="/insights">Insights</FooterInternalLink>
        </InternalColumn>
        <InternalColumn>
          <FooterColumnLabel>About Us</FooterColumnLabel>
          <FooterInternalLink to="/about-us/our-leadership">Leadership</FooterInternalLink>
          <FooterInternalLink to="/careers">Careers</FooterInternalLink>
          <FooterInternalLink to="/contact">Contact Us</FooterInternalLink>
        </InternalColumn>
        {renderLocaleLinks()}
        <MobileSocial>{renderSocialSection()}</MobileSocial>
      </RightContent>
    </Footer>
  );
};

const LeftContent = styled.div`
  display: flex;
  justify-content: space-between;
  height: 345px;
  flex-direction: column;
  width: 440px;
  padding: 0px;

  @media screen and (max-width: ${media.mediumBottom}) {
    height: auto;
    width: auto;
    margin-right: 0px;
  }
`;

const FooterText = styled(Text)`
  padding-top: 20px;
  color: ${colors.pureWhite};
`;

const SocialLinkContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;

  @media screen and (max-width: ${media.mediumBottom}) {
    gap: 16px;
  }
`;

const SocialIcon = styled.img`
  margin: 0;
  width: 20px;
  height: 20px;
`;

const DesktopSocial = styled.div`
  @media screen and (max-width: ${media.mediumBottom}) {
    display: none;
  }
`;

const MobileSocial = styled.div`
  padding-top: 40px;

  @media screen and (min-width: ${media.mediumBottom}) {
    display: none;
  }
`;

const RightContent = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 680px;
  flex-wrap: wrap;
  flex: 1;
  gap: 40px;

  @media screen and (max-width: ${media.mediumBottom}) {
    flex-direction: column;
  }
`;

const InternalColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media screen and (max-width: ${media.mediumTop}) {
    width: auto;
  }
`;

const FooterColumnLabel = styled(Label)`
  color: ${colors.pureWhite};
  margin-bottom: 0px;
  font-size: 16px;
`;

const FooterInternalLink = styled(InternalLink)`
  font-weight: 400;
  text-transform: capitalize;
  font-family: Lato, sans-serif;
  &:hover {
    color: ${colors.pureWhite};
  }

  @media screen and (max-width: ${media.smallTop}) {
    line-height: 24px;
  }
`;

export default AppFooter;
